// Pure helper deciding which camera locations the /cameras grid renders.
// A location is hidden only once its per-location camera fetch resolves to
// zero cameras (never while loading/errored). Framework-free for unit tests.

/** Per-location camera fetch state, keyed by location_id in the grid. */
export type LocationCameraState =
  | { kind: "loading" }
  | { kind: "ready"; cameras: unknown[] }
  | { kind: "error"; message: string };

/** Minimal shape the filter needs: anything carrying a `location_id`. */
export interface HasLocationId {
  location_id: string;
}

/** Confirmed empty: the camera fetch resolved with zero cameras. */
function isConfirmedEmpty(state: LocationCameraState | undefined): boolean {
  return state?.kind === "ready" && state.cameras.length === 0;
}

/**
 * Locations to render. `showEmpty` false (default) drops confirmed-empty ones;
 * order and identity are preserved.
 */
export function visibleLocations<T extends HasLocationId>(
  locations: T[],
  byLocation: Record<string, LocationCameraState>,
  showEmpty: boolean,
): T[] {
  if (showEmpty) return locations;
  return locations.filter(
    (loc) => !isConfirmedEmpty(byLocation[loc.location_id]),
  );
}

/**
 * In-scope locations exist but the hide-empty filter left none visible. Drives
 * the "no cameras match your access" banner — distinct from `locations.length
 * === 0` ("no locations discovered").
 */
export function allLocationsHidden<T extends HasLocationId>(
  locations: T[],
  byLocation: Record<string, LocationCameraState>,
  showEmpty: boolean,
): boolean {
  if (showEmpty) return false;
  if (locations.length === 0) return false;
  return visibleLocations(locations, byLocation, showEmpty).length === 0;
}
